import { LOCALE_ID, Injectable } from '@angular/core';
import { MAT_DATE_LOCALE, MatDateFormats, DateAdapter } from '@angular/material/core';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';

type LuxonDateTime = ReturnType<LuxonDateAdapter['today']>;

export const LATAM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: ['d/L/yyyy', 'dd/LL/yyyy', 'd/L/yy', 'dd/LL/yy'],
  },
  display: {
    dateInput: 'dd/LL/yyyy',
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'dd/LL/yyyy',
    monthYearA11yLabel: 'LLLL yyyy',
  }
};

/** Año de corte para pivoting de 2 dígitos: <= umbral → siglo XXI, > umbral → siglo XX */
const TWO_DIGIT_YEAR_CUTOFF = 30;
const COMPACT_DATE_PATTERN = /^(\d{2})(\d{2})(\d{2}|\d{4})$/;

@Injectable()
class LatamLuxonDateAdapter extends LuxonDateAdapter {
  override parse(value: any, parseFormat: string | string[]): LuxonDateTime | null {
    if (typeof value === 'string') {
      const compactMatch = value.trim().match(COMPACT_DATE_PATTERN);
      if (compactMatch) {
        const [, day, month, year] = compactMatch;
        const fullYear = year.length === 2 ? this.expandTwoDigitYear(Number(year)) : Number(year);
        return super.parse(`${day}/${month}/${fullYear}`, 'dd/LL/yyyy');
      }
    }

    const result = super.parse(value, parseFormat);
    if (!result || this.isValid(result)) return result;

    // Si falló, intentar los formatos yy con pivoting manual
    if (typeof value === 'string') {
      const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];
      const yyFormats = formats.filter(f => f.includes('yy') && !f.includes('yyyy'));
      for (const fmt of yyFormats) {
        const base = super.parse(value, fmt);
        if (base && this.getYear(base) > 0 && this.getYear(base) <= 99) {
          const y = this.getYear(base);
          const fullYear = this.expandTwoDigitYear(y);
          return this.createDate(fullYear, this.getMonth(base), this.getDate(base));
        }
      }
    }
    return result;
  }

  private expandTwoDigitYear(year: number): number {
    return year <= TWO_DIGIT_YEAR_CUTOFF ? 2000 + year : 1900 + year;
  }
}

export const DATE_PROVIDERS = [
  { provide: LOCALE_ID, useValue: 'es-AR' },
  { provide: MAT_DATE_LOCALE, useExisting: LOCALE_ID },
  provideLuxonDateAdapter(LATAM_DATE_FORMATS),
  { provide: DateAdapter, useClass: LatamLuxonDateAdapter, deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS] },
];
